import { Fragment, useEffect, useState } from 'react';
import {
  ShieldCheck, AlertTriangle, CheckCircle2, X, Loader2, User, Mail, Phone, Landmark,
  Check, XCircle, Eye, FileText
} from 'lucide-react';
import dayjs from 'dayjs';
import toast from 'react-hot-toast';
import { apiDraftsApi } from '../../utils/api';
import ConfirmModal from '../../components/ConfirmModal';

// Revisión de Finance 360 sobre una factura que llegó por la API (27-oct-2026).
// Muestra lo que no cuadra contra el catálogo y los mínimos de Hacienda del cliente;
// quien factura decide si la acepta así o la rechaza con el motivo.

const TIPO_ID = { '01': 'Física', '02': 'Jurídica', '03': 'DIMEX', '04': 'NITE', '05': 'Extranjero' };
const MAX_MOTIVO = 500;

const formatMoney = (amount, currency = 'CRC') => {
  const num = parseFloat(amount) || 0;
  return currency === 'USD'
    ? `$${num.toLocaleString('en-US', { minimumFractionDigits: 2 })}`
    : `₡${num.toLocaleString('es-CR', { minimumFractionDigits: 2 })}`;
};

function Cabys({ codigo, descripcion, marcar }) {
  if (!codigo) return <span className="text-gray-400">—</span>;
  return (
    <div className="min-w-0">
      <p className={`font-mono text-xs ${marcar ? 'text-amber-700 font-semibold' : 'text-gray-800'}`}>{codigo}</p>
      <p className="text-[11px] text-gray-500 truncate" title={descripcion || 'No existe en el catálogo de Hacienda'}>
        {descripcion || 'No existe en el catálogo de Hacienda'}
      </p>
    </div>
  );
}

export default function RevisionFacturaApiModal({ doc, inicioRechazo = false, onClose, onCambio, onVerFactura }) {
  const [rev, setRev] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [rechazando, setRechazando] = useState(false);
  const [motivo, setMotivo] = useState('');
  const [procesando, setProcesando] = useState(false);
  const [confirmarAceptar, setConfirmarAceptar] = useState(false);

  const pendiente = doc.apiReviewStatus === 'pending_review';

  useEffect(() => {
    let vivo = true;
    (async () => {
      try {
        const r = await apiDraftsApi.revision(doc.id);
        if (!vivo) return;
        const data = r.data?.data;
        setRev(data);
        setMotivo(data?.motivoSugerido || '');
        if (inicioRechazo && pendiente) setRechazando(true);
      } catch (e) {
        toast.error(e.response?.data?.error || 'No se pudo revisar la factura');
      } finally { if (vivo) setCargando(false); }
    })();
    return () => { vivo = false; };
  }, [doc.id]);

  const obs = rev?.observaciones || [];
  const obsCliente = obs.filter(o => o.ambito === 'cliente');
  const obsLineas = obs.filter(o => o.ambito !== 'cliente');
  const lineasConObs = (rev?.lineas || []).filter(l => l.observaciones.length > 0).length;

  const aceptar = async () => {
    setProcesando(true);
    try {
      await apiDraftsApi.approve(doc.id, { observaciones: obs.length });
      toast.success(obs.length ? 'Aceptada con observaciones: queda aprobada para facturar' : 'Aprobada para facturar');
      onCambio?.(); onClose();
    } catch (e) {
      toast.error(e.response?.data?.error || 'No se pudo aprobar');
    } finally { setProcesando(false); setConfirmarAceptar(false); }
  };

  const rechazar = async () => {
    const texto = motivo.trim();
    if (!texto) { toast.error('Escriba el motivo del rechazo'); return; }
    setProcesando(true);
    try {
      await apiDraftsApi.reject(doc.id, texto.slice(0, MAX_MOTIVO));
      toast.success('Rechazada: el integrador recibe el motivo');
      onCambio?.(); onClose();
    } catch (e) {
      toast.error(e.response?.data?.error || 'No se pudo rechazar');
    } finally { setProcesando(false); }
  };

  const hac = rev?.clienteHacienda;
  const cli = rev?.cliente;

  return (
    <>
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-7xl max-h-[92vh] flex flex-col" onClick={e => e.stopPropagation()}>
        {/* Encabezado */}
        <div className="flex items-center justify-between gap-4 px-6 py-4 border-b shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-xl border border-indigo-300 flex items-center justify-center shrink-0">
              <ShieldCheck className="h-5 w-5 text-indigo-600" />
            </div>
            <div className="min-w-0">
              <h3 className="text-lg font-semibold text-gray-900 leading-tight">Revisión de Finance 360</h3>
              <p className="text-xs text-gray-500 truncate">
                {doc.receptorNombre} · {doc.consecutivo || 'Borrador'} · {dayjs(doc.fechaEmision).format('DD/MM/YYYY')} · {formatMoney(doc.totalComprobante, doc.moneda)} · {doc.apiCredentialName || 'API'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            {!cargando && (obs.length === 0 ? (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full border border-emerald-400 text-emerald-700 text-xs font-semibold">
                <CheckCircle2 className="h-3.5 w-3.5" /> Sin observaciones
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full border border-amber-400 text-amber-700 text-xs font-semibold">
                <AlertTriangle className="h-3.5 w-3.5" /> {obs.length} {obs.length === 1 ? 'observación' : 'observaciones'}
              </span>
            ))}
            <button onClick={onClose} className="p-2 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100"><X className="h-5 w-5" /></button>
          </div>
        </div>

        {cargando ? (
          <div className="flex-1 flex items-center justify-center py-20 text-gray-500 gap-2">
            <Loader2 className="h-5 w-5 animate-spin" /> Revisando contra el catálogo y Hacienda…
          </div>
        ) : !rev ? (
          <div className="flex-1 py-20 text-center text-gray-500">No se pudo revisar la factura.</div>
        ) : (
          <div className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-[22rem_1fr] gap-5 px-6 py-5 overflow-y-auto lg:overflow-hidden">
            {/* Izquierda: cliente y resumen */}
            <div className="space-y-4 min-w-0 lg:overflow-y-auto">
              <section className="rounded-xl border border-gray-200 p-4">
                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Cliente</h4>
                {cli ? (
                  <div className="space-y-2 text-sm">
                    <p className="font-semibold text-gray-900 flex items-start gap-2"><User className="h-4 w-4 text-gray-400 mt-0.5 shrink-0" />{cli.nombre || '—'}</p>
                    <p className="text-gray-700 flex items-center gap-2"><FileText className="h-4 w-4 text-gray-400 shrink-0" />{TIPO_ID[cli.tipoIdentificacion] || cli.tipoIdentificacion || '—'} · <span className="font-mono">{cli.identificacion || '—'}</span></p>
                    <p className="text-gray-700 flex items-center gap-2 min-w-0"><Mail className="h-4 w-4 text-gray-400 shrink-0" /><span className="truncate">{cli.correo || 'Sin correo'}</span></p>
                    <p className="text-gray-700 flex items-center gap-2"><Phone className="h-4 w-4 text-gray-400 shrink-0" />{cli.telefono || 'Sin teléfono'}</p>
                    <div className="pt-2 mt-2 border-t border-gray-100 flex items-start gap-2 text-xs">
                      <Landmark className="h-4 w-4 text-gray-400 shrink-0" />
                      {!hac ? <span className="text-gray-500">Hacienda: no aplica para este tipo de identificación</span>
                        : hac.disponible === false ? <span className="text-gray-500">Hacienda no respondió: no se pudo verificar la cédula</span>
                        : hac.encontrado === false ? <span className="text-amber-700 font-medium">Hacienda no encuentra esta cédula</span>
                        : <span className="text-gray-600">Hacienda: <span className="font-medium text-gray-800">{hac.nombre}</span>{hac.regimen ? ` · ${hac.regimen}` : ''}</span>}
                    </div>
                  </div>
                ) : <p className="text-sm text-gray-500">La factura no tiene cliente.</p>}
                {obsCliente.length > 0 && (
                  <ul className="mt-3 space-y-1.5">
                    {obsCliente.map((o, i) => (
                      <li key={i} className="flex items-start gap-2 text-xs text-amber-800 border border-amber-300 rounded-lg px-2.5 py-1.5">
                        <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" /><span>{o.mensaje}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </section>

              <section className="rounded-xl border border-gray-200 p-4">
                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Resumen</h4>
                <dl className="grid grid-cols-2 gap-3 text-sm">
                  <div><dt className="text-xs text-gray-500">Líneas revisadas</dt><dd className="text-lg font-semibold text-gray-900">{rev.lineas.length}</dd></div>
                  <div><dt className="text-xs text-gray-500">Líneas con observaciones</dt><dd className={`text-lg font-semibold ${lineasConObs ? 'text-amber-700' : 'text-gray-900'}`}>{lineasConObs}</dd></div>
                  <div><dt className="text-xs text-gray-500">Observaciones del cliente</dt><dd className={`text-lg font-semibold ${obsCliente.length ? 'text-amber-700' : 'text-gray-900'}`}>{obsCliente.length}</dd></div>
                  <div><dt className="text-xs text-gray-500">Estado</dt><dd className="text-sm font-medium text-gray-800 mt-1">{pendiente ? 'Pendiente de revisión' : doc.apiReviewStatus === 'approved' ? 'Aprobada' : 'Rechazada'}</dd></div>
                </dl>
                {doc.apiReviewStatus === 'rejected' && doc.apiReviewReason && (
                  <p className="mt-3 text-xs text-red-700 border border-red-300 rounded-lg px-2.5 py-1.5">{doc.apiReviewReason}</p>
                )}
              </section>
            </div>

            {/* Derecha: líneas */}
            <div className="min-w-0 flex flex-col min-h-0">
              <div className="rounded-xl border border-gray-200 overflow-hidden flex flex-col min-h-0">
                <div className="overflow-auto">
                  {/* table-fixed + whiteSpace normal: el tema pone las celdas en una línea y la tabla se salía a lo ancho */}
                  <table className="w-full text-sm table-fixed">
                    <colgroup>
                      <col style={{ width: '2.5rem' }} /><col /><col style={{ width: '12rem' }} /><col style={{ width: '12rem' }} />
                      <col style={{ width: '6rem' }} /><col style={{ width: '8rem' }} />
                    </colgroup>
                    <thead className="sticky top-0 bg-white border-b z-10">
                      <tr className="text-left text-xs text-gray-500">
                        <th className="px-3 py-2.5 font-semibold">#</th>
                        <th className="px-3 py-2.5 font-semibold">Producto</th>
                        <th className="px-3 py-2.5 font-semibold">CABYS de la factura</th>
                        <th className="px-3 py-2.5 font-semibold">CABYS del catálogo</th>
                        <th className="px-3 py-2.5 font-semibold">Unidad</th>
                        <th className="px-3 py-2.5 font-semibold text-right">Subtotal</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rev.lineas.map((l, idx) => {
                        const tieneObs = l.observaciones.length > 0;
                        const cabysMal = l.observaciones.some(o => o.tipo === 'cabys_distinto' || o.tipo === 'cabys_inexistente');
                        const unidadMal = l.observaciones.some(o => o.tipo === 'unidad_distinta');
                        return (
                          <Fragment key={idx}>
                          <tr className={`align-top ${tieneObs ? '' : 'border-b border-gray-100'}`}>
                            <td className="px-3 py-2.5 text-gray-500">
                              {tieneObs ? <AlertTriangle className="h-4 w-4 text-amber-600" /> : <CheckCircle2 className="h-4 w-4 text-emerald-600" />}
                            </td>
                            <td className="px-3 py-2.5" style={{ whiteSpace: 'normal' }}>
                              <p className="text-gray-900 leading-snug" title={l.detalle}
                                style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', whiteSpace: 'normal' }}>{l.detalle}</p>
                              <p className="text-[11px] text-gray-500 font-mono">{l.codigoComercial || 'sin código'} · cant. {l.cantidad}</p>
                            </td>
                            <td className="px-3 py-2.5" style={{ whiteSpace: 'normal' }}><Cabys codigo={l.cabys} descripcion={l.cabysDescripcion} marcar={cabysMal} /></td>
                            <td className="px-3 py-2.5" style={{ whiteSpace: 'normal' }}>
                              {l.catalogo ? <Cabys codigo={l.catalogo.cabys} descripcion={l.catalogo.cabysDescripcion} /> : <span className="text-xs text-gray-400">No está en el catálogo</span>}
                            </td>
                            <td className="px-3 py-2.5 text-xs">
                              <span className={unidadMal ? 'text-amber-700 font-semibold' : 'text-gray-800'}>{l.unidadMedida || '—'}</span>
                              {unidadMal && l.catalogo?.unidadMedida && <span className="text-gray-500"> → {l.catalogo.unidadMedida}</span>}
                            </td>
                            <td className="px-3 py-2.5 text-right tabular-nums text-gray-800">{formatMoney(l.subtotal, doc.moneda)}</td>
                          </tr>
                          {tieneObs && (
                            <tr className="border-b border-gray-100">
                              <td />
                              <td colSpan={5} className="px-3 pb-3 pt-0" style={{ whiteSpace: 'normal' }}>
                                <ul className="border border-amber-300 rounded-lg px-3 py-2 space-y-1">
                                  {l.observaciones.map((o, i) => (
                                    <li key={i} className="text-xs text-amber-800 leading-snug flex items-start gap-1.5">
                                      <span className="mt-1 h-1 w-1 rounded-full bg-amber-500 shrink-0" />{o.mensaje}
                                    </li>
                                  ))}
                                </ul>
                              </td>
                            </tr>
                          )}
                          </Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {rechazando && pendiente && (
                <div className="mt-4 rounded-xl border border-red-300 p-4 shrink-0">
                  <div className="flex items-center justify-between mb-2">
                    <label htmlFor="motivo-rechazo" className="text-sm font-semibold text-red-700">Motivo del rechazo (lo recibe el integrador)</label>
                    <span className={`text-xs ${motivo.length > MAX_MOTIVO ? 'text-red-600 font-semibold' : 'text-gray-400'}`}>{motivo.length}/{MAX_MOTIVO}</span>
                  </div>
                  <textarea id="motivo-rechazo" value={motivo} onChange={e => setMotivo(e.target.value)} rows={3} autoFocus
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-200 focus:border-red-400 resize-none" />
                </div>
              )}
            </div>
          </div>
        )}

        {/* Pie */}
        <div className="flex items-center justify-between gap-3 px-6 py-4 border-t shrink-0">
          <button onClick={() => onVerFactura?.(doc)} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 flex items-center gap-2">
            <Eye className="h-4 w-4" /> Ver factura
          </button>
          <div className="flex items-center gap-3">
            {!pendiente ? (
              <button onClick={onClose} className="px-5 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 text-sm font-medium">Cerrar</button>
            ) : rechazando ? (
              <>
                <button onClick={() => setRechazando(false)} disabled={procesando} className="px-5 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 text-sm font-medium">Cancelar</button>
                <button onClick={rechazar} disabled={procesando || !motivo.trim() || motivo.length > MAX_MOTIVO}
                  className="px-5 py-2 border-2 border-red-500 text-red-600 rounded-lg text-sm font-semibold hover:bg-red-50 disabled:opacity-50 flex items-center gap-2">
                  {procesando ? <Loader2 className="h-4 w-4 animate-spin" /> : <XCircle className="h-4 w-4" />} Confirmar rechazo
                </button>
              </>
            ) : (
              <>
                <button onClick={() => setRechazando(true)} disabled={cargando || procesando}
                  className="px-5 py-2 border-2 border-red-400 text-red-600 rounded-lg text-sm font-semibold hover:bg-red-50 disabled:opacity-50 flex items-center gap-2">
                  <XCircle className="h-4 w-4" /> Rechazar
                </button>
                <button onClick={() => (obs.length ? setConfirmarAceptar(true) : aceptar())} disabled={cargando || procesando}
                  className="px-5 py-2 border-2 border-emerald-500 text-emerald-700 rounded-lg text-sm font-semibold hover:bg-emerald-50 disabled:opacity-50 flex items-center gap-2">
                  {procesando ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} {obs.length ? 'Aceptar así' : 'Aprobar'}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>

      <ConfirmModal
        isOpen={confirmarAceptar}
        onClose={() => setConfirmarAceptar(false)}
        onConfirm={aceptar}
        title={`¿Aceptar con ${obs.length} ${obs.length === 1 ? 'observación' : 'observaciones'}?`}
        message="La factura queda aprobada para facturar tal como vino del integrador. Queda registrado que se aceptó con observaciones de la revisión de Finance 360."
        confirmText="Sí, aceptar así"
        cancelText="No, volver"
        variant="warning"
      />
    </>
  );
}
